/**
 * 거래처 마스터 본문 (PartnerCompanyPage 에서 사용 · 읽기 sales 이상 · 쓰기 admin + mgmt — v2.318.0 전에는 admin + mgmt 만 들어왔다).
 *   영업 계정(canEdit=false)에는 [+ 새 거래처 추가] · 줄 끝 ▼(수정·삭제) · 통장사본 칸 · 첨부/교체/삭제 단추가 없고,
 *   사용/미사용 · 자동 (주) 통일은 글자로만 보인다. 서버(/admin/partner-companies 쓰기 · attachments 통장사본)도 따로 막는다.
 *
 * 거래처 마스터(partner_company) CRUD. 이 목록이:
 *   · 영업·매입·견적 회사명칸 자동완성 후보
 *   · '자동 (주) 통일' ON 인 이름은 입력 시 변형을 그 표기로 자동 보정하는 기준
 *
 * 거래처별 사업자등록번호·주소 + 사업자등록증 PDF(📄 다운로드/첨부) 관리.
 */

import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as adminApi from '../lib/admin-api';
import type { PartnerCompanyRow } from '../lib/admin-api';
import { uploadAttachment, deleteAttachment, previewAttachment } from '../lib/attachment-api';
import type { NasAutoSaveResult } from '../lib/attachment-api';
import type { PartnerKind } from '../lib/admin-api';
import { PartnerDetailModal } from './PartnerDetailModal';
import { applyColState, ColumnHeader, useColState, type Column } from './DataTable';
import { ExcelModeGroup } from './ExcelModeButton';
import { ResetMenu } from './ResetButton';
import * as prefsApi from '../lib/preferences-api';
import { useExcelModeTable } from '../lib/useExcelModeTable';
import { useProgressiveRows } from '../lib/useProgressiveRows';
import { useAuth } from '../lib/auth-context';
import type { UserPreferences } from '../lib/types';
import { ApiCallError } from '../lib/api';
import { LoadingLines } from './ui/Skeleton';
import { RowMenu } from './ui/RowMenu';

/** 「그래도 저장」을 안 고른 경우 — 실패 알림을 띄우지 않는다 */
class DupCancelled extends Error {}

/**
 * 같은 사업자번호·비슷한 이름이면 서버가 확인을 요구한다 (v2.231.0 · 사용자 결정 D-3 — 막지 않고 경고만).
 * 물어보고 「확인」이면 confirmDuplicate 로 다시 보낸다.
 */
async function withDupConfirm<T>(run: (confirmDuplicate: boolean) => Promise<T>): Promise<T> {
  try {
    return await run(false);
  } catch (e) {
    const reason = e instanceof ApiCallError ? (e.details as { reason?: string } | undefined)?.reason : undefined;
    if (reason !== 'PARTNER_DUP_SUSPECT') throw e;
    if (!window.confirm(`${(e as Error).message}\n\n그래도 저장할까요?`)) throw new DupCancelled();
    return run(true);
  }
}

const KIND_LABEL: Record<PartnerKind, string> = { supplier: '매입처', customer: '고객사', both: '양쪽' };
const KIND_BADGE: Record<PartnerKind, string> = {
  supplier: 'bg-sky-100 text-sky-700',
  customer: 'bg-amber-100 text-amber-700',
  both: 'bg-violet-100 text-violet-700',
};
/** 유형 정렬 순서 — 글자순(고객사·매입처·양쪽)이 아니라 뜻이 통하는 순서로 */
const KIND_ORDER: Record<PartnerKind, number> = { supplier: 0, customer: 1, both: 2 };

/* ── 표 칸 정의 — 머리글 정렬 · 🔍 필터 · 📐 내 표 설정 (v2.148.0 · 담당자 탭과 같은 방식) ──────
 *   ⭐`defaultOn:true` = 새 사용자에게 보이는 기본 표 = 종전 10칸 그대로.
 *   ⭐**accessor 가 정렬·🔍 목록의 값**이다 — 첨부·체크박스 칸도 「있음/없음」처럼 사람이 고를 글자를 돌려준다.
 *     (빈 글자만 돌려주면 🔍 목록이 빈칸 하나로 뭉개진다)
 */
type CompanyColumn = Column<PartnerCompanyRow> & {
  defaultOn: boolean;
  toggleLabel?: string;
  defaultWidthPx?: number;
  cellClass?: string;
};

interface CompanyColumnCtx {
  /** 쓰기 권한(admin·mgmt) — 없으면 단추 칸·통장사본 칸을 빼고 토글은 글자로 (v2.318.0) */
  canEdit: boolean;
  busyCertId: number | null;
  busyBankbookId: number | null;
  saving: boolean;
  onOpenDetail: (id: number) => void;
  onCertUpload: (row: PartnerCompanyRow, file: File) => void;
  onCertDelete: (row: PartnerCompanyRow) => void;
  onBankbookUpload: (row: PartnerCompanyRow, file: File) => void;
  onBankbookDelete: (row: PartnerCompanyRow) => void;
  onToggleAutoFix: (row: PartnerCompanyRow, next: boolean) => void;
  onToggleActive: (row: PartnerCompanyRow) => void;
  onEdit: (row: PartnerCompanyRow) => void;
  onDelete: (row: PartnerCompanyRow) => void;
}

function buildCompanyColumns(ctx: CompanyColumnCtx): CompanyColumn[] {
  const cols: CompanyColumn[] = [
    {
      key: 'name', label: '거래처명', type: 'text', defaultOn: true, defaultWidthPx: 220, cellClass: 'font-medium',
      accessor: (row) => row.name,
      render: (row) => (
        <>
          <button
            type="button"
            onClick={() => ctx.onOpenDetail(row.id)}
            className="text-left text-blue-700 hover:underline"
            title="거래처 상세 — 연결된 영업/매입/유지보수/견적 보기"
          >
            {row.name}
          </button>
          {!row.isActive && <span className="ml-1.5 text-[11px] text-slate-500">(미사용)</span>}
        </>
      ),
    },
    {
      key: 'kind', label: '유형', type: 'text', align: 'center', defaultOn: true, defaultWidthPx: 80, nowrapHeader: true,
      accessor: (row) => KIND_LABEL[row.kind],
      sortAccessor: (row) => KIND_ORDER[row.kind],
      render: (row) => (
        <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${KIND_BADGE[row.kind]}`}>
          {KIND_LABEL[row.kind]}
        </span>
      ),
    },
    {
      key: 'businessNumber', label: '사업자등록번호', type: 'text', defaultOn: true, defaultWidthPx: 144, nowrapHeader: true,
      cellClass: 'text-slate-600 font-mono text-xs',
      accessor: (row) => row.businessNumber ?? '',
      render: (row) => row.businessNumber || <span className="text-slate-300">-</span>,
    },
    {
      key: 'address', label: '주소', type: 'text', defaultOn: true, defaultWidthPx: 340, cellClass: 'text-slate-600 text-xs max-w-0',
      accessor: (row) => row.address ?? '',
      render: (row) => <div className="truncate" title={row.address ?? ''}>{row.address || <span className="text-slate-300">-</span>}</div>,
    },
    {
      key: 'taxEmail', label: '세금계산서 이메일', type: 'text', defaultOn: true, defaultWidthPx: 200, cellClass: 'text-slate-600 text-xs max-w-0',
      // ⭐셀에 ②만 있는 거래처도 있다 — accessor 가 ① 만 보면 「빈 값」으로 뭉쳐 🔍·정렬이 화면과 어긋난다 (v2.149.0)
      accessor: (row) => row.taxInvoiceEmail || row.taxInvoiceEmail2 || '',
      render: (row) => (
        row.taxInvoiceEmail || row.taxInvoiceEmail2 ? (
          <div className="flex flex-col leading-tight">
            {row.taxInvoiceEmail && <span className="truncate" title={row.taxInvoiceEmail}>{row.taxInvoiceEmail}</span>}
            {row.taxInvoiceEmail2 && <span className="truncate text-slate-400" title={row.taxInvoiceEmail2}>{row.taxInvoiceEmail2}</span>}
          </div>
        ) : (
          <span className="text-slate-300">-</span>
        )
      ),
    },
    {
      key: 'cert', label: '사업자등록증', type: 'text', align: 'center', defaultOn: true, defaultWidthPx: 112, nowrapHeader: true,
      accessor: (row) => (row.certAttachmentId ? '있음' : '없음'),
      render: (row) => <CertCell row={row} canEdit={ctx.canEdit} busy={ctx.busyCertId === row.id} onUpload={ctx.onCertUpload} onDelete={ctx.onCertDelete} />,
    },
    {
      key: 'bankbook', label: '통장사본', type: 'text', align: 'center', defaultOn: true, defaultWidthPx: 112, nowrapHeader: true,
      accessor: (row) => (row.bankbookAttachmentId ? '있음' : '없음'),
      render: (row) => <BankbookCell row={row} busy={ctx.busyBankbookId === row.id} onUpload={ctx.onBankbookUpload} onDelete={ctx.onBankbookDelete} />,
    },
    {
      key: 'autoFix', label: '자동 (주) 통일', type: 'text', align: 'center', defaultOn: true, defaultWidthPx: 96,
      accessor: (row) => (row.autoFix ? '켬' : '끔'),
      render: (row) => (
        <input
          type="checkbox"
          checked={row.autoFix}
          disabled={ctx.saving || !ctx.canEdit}
          onChange={(e) => ctx.onToggleAutoFix(row, e.target.checked)}
          title="체크하면 이 표기로 입력 변형을 자동 통일"
        />
      ),
    },
    {
      key: 'active', label: '사용', type: 'text', align: 'center', defaultOn: true, defaultWidthPx: 80, nowrapHeader: true,
      accessor: (row) => (row.isActive ? '사용' : '미사용'),
      render: (row) => ctx.canEdit ? (
        <button
          type="button"
          onClick={() => ctx.onToggleActive(row)}
          className={row.isActive ? 'text-emerald-600 text-xs hover:underline' : 'text-slate-500 text-xs hover:underline'}
          title="자동완성 후보에서 숨기기/보이기"
        >
          {row.isActive ? '● 사용' : '○ 미사용'}
        </button>
      ) : (
        <span className={row.isActive ? 'text-emerald-600 text-xs' : 'text-slate-500 text-xs'}>{row.isActive ? '● 사용' : '○ 미사용'}</span>
      ),
    },

    /* ── 기본은 꺼져 있는 칸 — [📐 내 표 설정] 에서 켠다 ── */
    { key: 'taxEmail2', label: '세금계산서 이메일 2', type: 'text', defaultOn: false, defaultWidthPx: 180, cellClass: 'text-slate-600 text-xs max-w-0', accessor: (row) => row.taxInvoiceEmail2 ?? '', render: (row) => <div className="truncate" title={row.taxInvoiceEmail2 ?? ''}>{row.taxInvoiceEmail2 || <span className="text-slate-300">-</span>}</div> },
    { key: 'note', label: '비고', type: 'text', defaultOn: false, defaultWidthPx: 180, cellClass: 'text-slate-600 text-xs max-w-0', accessor: (row) => row.note ?? '', render: (row) => <div className="truncate" title={row.note ?? ''}>{row.note || <span className="text-slate-300">-</span>}</div> },
    { key: 'updatedAt', label: '최종 수정', type: 'date', defaultOn: false, defaultWidthPx: 96, nowrapHeader: true, cellClass: 'text-slate-500 text-xs', accessor: (row) => (row.updatedAt ?? '').slice(0, 10), render: (row) => <span className="tabular-nums whitespace-nowrap">{(row.updatedAt ?? '').slice(0, 10) || '-'}</span> },

    {
      // 단추 칸 — 값이 없으므로 정렬·🔍 를 아예 안 붙인다 (v2.149.0 · noSort/noFilter)
      key: 'actions', label: '', toggleLabel: '단추 (수정 · 삭제)', type: 'text', align: 'center', defaultOn: true, defaultWidthPx: 44, nowrapHeader: true,
      cellClass: 'whitespace-nowrap',
      noSort: true, noFilter: true,
      accessor: () => '',
      // v2.317.0 · 줄 끝 ▼ 메뉴 한 칸(Salesforce 식) — 전에는 「수정 | 삭제」 글자 단추 두 개
      render: (row) => (
        <RowMenu
          items={[
            { label: '수정', onClick: () => ctx.onEdit(row) },
            { label: '삭제', tone: 'danger', onClick: () => ctx.onDelete(row) },
          ]}
        />
      ),
    },
  ];
  // 읽기 전용(영업)이면 통장사본(은행 계좌 서류)과 단추 칸을 뺀다 — 서버도 통장사본을 null 로 준다 (v2.318.0)
  return ctx.canEdit ? cols : cols.filter((c) => c.key !== 'bankbook' && c.key !== 'actions');
}

/** 정렬·필터 계산과 연쇄 필터 목록에 쓰는 **전체 칸** — 숨긴 칸의 필터도 살아 있어야 한다 */
const COMPANY_COLUMNS: CompanyColumn[] = buildCompanyColumns({
  canEdit: true,
  busyCertId: null, busyBankbookId: null, saving: false,
  onOpenDetail: () => undefined, onCertUpload: () => undefined, onCertDelete: () => undefined,
  onBankbookUpload: () => undefined, onBankbookDelete: () => undefined,
  onToggleAutoFix: () => undefined, onToggleActive: () => undefined,
  onEdit: () => undefined, onDelete: () => undefined,
});
const COMPANY_DEFAULT_SORT = [{ key: 'name', dir: 'asc' as const }];
/** 「액션」 칸 — ⚙ 에서 끌 수 없다(끄면 [수정]·[삭제] 로 가는 길이 사라진다) */
const COMPANY_ACTION_KEY = 'actions';

export function PartnerCompanyTab() {
  const qc = useQueryClient();
  const { user } = useAuth();
  // 쓰기 권한 — admin·mgmt. 영업은 읽기만(v2.318.0 · 결정 ⑤). ⛔화면 가림으로 끝내지 않는다 — 서버 쓰기 가드는 그대로 admin·mgmt.
  const canEdit = user?.role === 'admin' || user?.role === 'mgmt';
  const q = useQuery({
    queryKey: ['admin', 'partnerCompanies'],
    queryFn: adminApi.listPartnerCompanies,
    staleTime: 30_000,
  });
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState<'' | PartnerKind>('');
  // 추가 폼은 기본 접힘 — 검색창과 혼동해 실수로 거래처가 생성되던 문제 방지. [+ 새 거래처 추가] 버튼으로만 펼침.
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newBizNo, setNewBizNo] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [newKind, setNewKind] = useState<PartnerKind>('supplier');
  const [newAutoFix, setNewAutoFix] = useState(false);
  const [newEmail1, setNewEmail1] = useState('');
  const [newEmail2, setNewEmail2] = useState('');
  const [editing, setEditing] = useState<PartnerCompanyRow | null>(null);
  const [busyCertId, setBusyCertId] = useState<number | null>(null);
  const [busyBankbookId, setBusyBankbookId] = useState<number | null>(null);
  /**
   * 방금 올린 서류의 NAS 보관 결과 (v2.96.0).
   * ⚠표 **바깥**(표 위 한 줄)에만 쓴다 — 칸 안에 넣으면 행 높이가 흔들린다.
   */
  const [nasNote, setNasNote] = useState<string | null>(null);
  // ?open=<id> 로 들어오면 그 거래처 360 을 바로 연다 (v2.319.0 · 헤더 검색·최근 본 항목·할 일 연결이 보낸다)
  const [detailId, setDetailId] = useState<number | null>(() => {
    try { const o = new URLSearchParams(window.location.search).get('open'); const n = o ? Number(o) : NaN; return Number.isFinite(n) && n > 0 ? n : null; } catch { return null; }
  });
  // 사업자등록증 사본 자동 판독 (추가 폼) — 업로드한 사본은 등록 완료 시 거래처에 첨부됨
  const [certFile, setCertFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parseNote, setParseNote] = useState<{ kind: 'ok' | 'warn' | 'err'; text: string } | null>(null);
  const certInputRef = useRef<HTMLInputElement>(null);
  // 판독 세대 번호 — 폼을 닫거나 새로 열면 증가시켜, 진행 중이던 판독 결과가
  // 닫힌/새 폼에 뒤늦게 채워지는 것을 무효화한다.
  const parseSeqRef = useRef(0);

  // 추가 폼 열기/닫기 — 열 때 전체 초기화 (이전 회사의 사본·내용이 다음 등록에 섞이는 사고 방지)
  function openAddForm() {
    parseSeqRef.current++;
    setNewName(''); setNewBizNo(''); setNewAddress(''); setNewAutoFix(false); setNewKind('supplier');
    setNewEmail1(''); setNewEmail2('');
    setCertFile(null); setParseNote(null); setParsing(false);
    if (certInputRef.current) certInputRef.current.value = '';
    setShowAddForm(true);
  }
  function closeAddForm() {
    parseSeqRef.current++; // 진행 중 판독 무효화
    setParsing(false);
    setShowAddForm(false);
  }

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'partnerCompanies'] });
    qc.invalidateQueries({ queryKey: ['lookup', 'partnerCompanies'] }); // 폼 자동완성 후보도 갱신
  };

  const createMut = useMutation({
    mutationFn: (input: Parameters<typeof adminApi.createPartnerCompany>[0]) =>
      withDupConfirm((confirmDuplicate) => adminApi.createPartnerCompany({ ...input, confirmDuplicate })),
    onSuccess: async (data: { id: number }) => {
      // 판독에 사용한 사업자등록증 사본을 새 거래처에 첨부 (best-effort — 실패해도 등록은 완료)
      if (certFile) {
        try {
          await uploadAttachment('partner', String(data.id), '사업자등록증', certFile, { replace: true });
        } catch (e) {
          alert(`거래처는 등록됐지만 사업자등록증 첨부에 실패했습니다: ${(e as Error).message}\n목록의 📄 버튼으로 다시 첨부할 수 있습니다.`);
        }
      }
      setNewName(''); setNewBizNo(''); setNewAddress(''); setNewAutoFix(false); setNewKind('supplier');
      setNewEmail1(''); setNewEmail2('');
      setCertFile(null); setParseNote(null);
      if (certInputRef.current) certInputRef.current.value = '';
      invalidate();
    },
    onError: (e: Error) => { if (!(e instanceof DupCancelled)) alert(`추가 실패: ${e.message}`); },
  });
  const updateMut = useMutation({
    mutationFn: ({ id, input }: { id: number; input: Parameters<typeof adminApi.updatePartnerCompany>[1] }) =>
      withDupConfirm((confirmDuplicate) => adminApi.updatePartnerCompany(id, { ...input, confirmDuplicate })),
    onSuccess: invalidate,
    onError: (e: Error) => { if (!(e instanceof DupCancelled)) alert(`수정 실패: ${e.message}`); },
  });
  const deleteMut = useMutation({
    mutationFn: adminApi.deletePartnerCompany,
    onSuccess: invalidate,
    onError: (e: Error) => alert(`삭제 실패: ${e.message}`),
  });

  const items = q.data?.items ?? [];
  const autoFixCount = items.filter((i) => i.autoFix).length;
  const certCount = items.filter((i) => i.certAttachmentId).length;
  const bankbookCount = items.filter((i) => i.bankbookAttachmentId).length;
  const kindCounts = useMemo(() => {
    const c = { supplier: 0, customer: 0, both: 0 };
    for (const i of items) c[i.kind] += 1;
    return c;
  }, [items]);
  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    return items.filter((i) => {
      if (kindFilter && i.kind !== kindFilter) return false;
      if (!t) return true;
      return (
        i.name.toLowerCase().includes(t) ||
        (i.businessNumber ?? '').includes(t) ||
        (i.address ?? '').toLowerCase().includes(t)
      );
    });
  }, [items, search, kindFilter]);

  // 사업자등록증 사본 업로드 → 자동 판독 → 폼 자동완성 (등록자가 검토·수정 후 [추가]로 완료)
  async function handleParseCert(file: File) {
    const seq = ++parseSeqRef.current;
    setCertFile(file);
    setParsing(true);
    setParseNote(null);
    try {
      const r = await adminApi.parsePartnerCert(file);
      if (seq !== parseSeqRef.current) return; // 폼이 닫혔거나 새로 열림 — 뒤늦은 결과 무시
      const filled: string[] = [];
      const missed: string[] = [];
      if (r.name) { setNewName(r.name); filled.push('거래처명'); } else missed.push('거래처명');
      if (r.bizNo) { setNewBizNo(r.bizNo); filled.push('사업자등록번호'); } else missed.push('사업자등록번호');
      if (r.address) { setNewAddress(r.address); filled.push('주소'); } else missed.push('주소');
      if (r.email) { setNewEmail1(r.email); filled.push('이메일①'); }
      if (filled.length === 0) {
        setParseNote({ kind: 'warn', text: '사본에서 항목을 찾지 못했습니다 — 직접 입력해 주세요. (사본은 등록 시 첨부됩니다)' });
      } else {
        const ocrWarn = r.source === 'ocr' ? ' ⚠ 스캔/사진 판독이라 글자 오차가 있을 수 있습니다 — 꼭 확인하세요.' : '';
        const missedTxt = missed.length > 0 ? ` (미인식: ${missed.join('·')})` : '';
        setParseNote({ kind: 'ok', text: `판독 완료 — ${filled.join('·')} 자동입력${missedTxt}. 내용을 검토·수정한 뒤 [+ 추가]를 누르면 사본 첨부와 함께 등록됩니다.${ocrWarn}` });
      }
    } catch (e) {
      if (seq !== parseSeqRef.current) return;
      setParseNote({ kind: 'err', text: `판독 실패: ${(e as Error).message} — 직접 입력해 주세요. (사본은 등록 시 첨부됩니다)` });
    } finally {
      if (seq === parseSeqRef.current) setParsing(false);
    }
  }

  function handleAdd() {
    // 판독 중이거나 이미 등록 진행 중이면 무시 (Enter 연타·중복 클릭 방지)
    if (createMut.isPending || parsing) return;
    const name = newName.trim();
    if (!name) return;
    createMut.mutate({
      name,
      autoFix: newAutoFix,
      kind: newKind,
      businessNumber: newBizNo.trim() || null,
      address: newAddress.trim() || null,
      taxInvoiceEmail: newEmail1.trim() || null,
      taxInvoiceEmail2: newEmail2.trim() || null,
    });
  }

  /**
   * NAS 보관 결과를 표 위 한 줄로 남긴다 (v2.96.0).
   * 실패 알림 창은 attachment-api 가 이미 띄우므로 여기서는 **성공만** 조용히 적는다.
   */
  function noteNasSave(partner: string, docKind: string, nas: NasAutoSaveResult | undefined): void {
    if (!nas?.attempted) { setNasNote(null); return; }
    setNasNote(
      nas.saved
        ? `💾 ${partner} ${docKind} — NAS 에도 보관했습니다 (${nas.path ?? ''})`
        : `⚠ ${partner} ${docKind} — NAS 보관 실패: ${nas.reason ?? '알 수 없는 이유'}`,
    );
  }

  function handleDelete(row: PartnerCompanyRow) {
    /*
     * ⚠거래처를 지우면 **번호로 물려 있던 연결이 조용히 끊긴다**
     *   (DB 제약이 ON DELETE SET NULL — 담당자의 소속 · 딜의 제안사 업체).
     *   예전 문구는 「기존 데이터는 그대로입니다」 였는데 그 부분이 사실과 달랐다 (v2.163.0).
     *   담당자·딜 자체가 지워지는 것은 아니므로 **막지는 않고**, 몇 건이 끊기는지 세어서 보여 준다.
     *   영업·매입처럼 거래처명을 **글자로** 들고 있는 자료는 정말 그대로다 — 그렇게 나눠서 적는다.
     */
    const links: string[] = [];
    if (row.contactCount > 0) links.push(`담당자 ${row.contactCount}명의 소속`);
    if (row.dealCount > 0) links.push(`딜등록 ${row.dealCount}건의 제안사`);
    // 명절선물 받는 곳 (v2.247.0) — 지우면 그 줄이 거래처별 비용에서 「거래처 미지정」으로 빠진다
    if ((row.giftCount ?? 0) > 0) links.push(`명절선물 받는 곳 ${row.giftCount}줄의 거래처`);

    // 첨부는 거래처와 함께 지워진다 (v2.232.0 · 점검 ③ D-S1 — 서버가 같은 트랜잭션에서 지운다)
    const files = [row.certAttachmentId ? '사업자등록증' : '', row.bankbookAttachmentId ? '통장사본' : ''].filter(Boolean);
    const fileLine = files.length ? `\n\n📎 ${files.join('·')} 첨부도 함께 지워집니다.` : '';
    const msg = (links.length
      ? `'${row.name}' 을(를) 거래처 목록에서 삭제할까요?\n\n`
        + `⚠ 지우면 ${links.join(' · ')} 연결이 끊어집니다.\n`
        + `   (${links.length === 1 ? '그 줄' : '그 줄들'}은 남지만 거래처 칸이 비워집니다. 되돌리려면 직접 다시 이어야 합니다.)\n\n`
        + `영업·매입 등 거래처 「이름」으로 적힌 자료는 그대로입니다.`
      : `'${row.name}' 을(를) 거래처 목록에서 삭제할까요?\n`
        + `(자동완성 후보에서 사라집니다. 연결된 담당자·딜등록은 없습니다.)`) + fileLine;
    if (!window.confirm(msg)) return;
    deleteMut.mutate(row.id);
  }

  async function handleCertUpload(row: PartnerCompanyRow, file: File) {
    setBusyCertId(row.id);
    try {
      const r = await uploadAttachment('partner', String(row.id), '사업자등록증', file, { replace: true });
      noteNasSave(row.name, '사업자등록증', r.nas);
      invalidate();
    } catch (e) {
      alert(`사업자등록증 첨부 실패: ${(e as Error).message}`);
    } finally {
      setBusyCertId(null);
    }
  }
  async function handleCertDelete(row: PartnerCompanyRow) {
    if (!row.certAttachmentId) return;
    if (!window.confirm(`'${row.name}' 의 사업자등록증 첨부를 삭제할까요?`)) return;
    setBusyCertId(row.id);
    try {
      await deleteAttachment(row.certAttachmentId);
      invalidate();
    } catch (e) {
      alert(`삭제 실패: ${(e as Error).message}`);
    } finally {
      setBusyCertId(null);
    }
  }

  async function handleBankbookUpload(row: PartnerCompanyRow, file: File) {
    setBusyBankbookId(row.id);
    try {
      const r = await uploadAttachment('partner', String(row.id), '통장사본', file, { replace: true });
      noteNasSave(row.name, '통장사본', r.nas);
      invalidate();
    } catch (e) {
      alert(`통장사본 첨부 실패: ${(e as Error).message}`);
    } finally {
      setBusyBankbookId(null);
    }
  }
  async function handleBankbookDelete(row: PartnerCompanyRow) {
    if (!row.bankbookAttachmentId) return;
    if (!window.confirm(`'${row.name}' 의 통장사본 첨부를 삭제할까요?`)) return;
    setBusyBankbookId(row.id);
    try {
      await deleteAttachment(row.bankbookAttachmentId);
      invalidate();
    } catch (e) {
      alert(`삭제 실패: ${(e as Error).message}`);
    } finally {
      setBusyBankbookId(null);
    }
  }

  /* ── 머리글 정렬 · 🔍 필터 · 📐 내 표 설정 (v2.148.0) ──────────────────────────────
   *   위쪽 검색·유형 칸까지 적용한 것이 `filtered`(= baseRows), 거기에 머리글 정렬·필터를 얹은 것이 `rows`.
   *   정렬·필터는 sessionStorage(세션 단위) · 켠 칸·순서·너비는 서버 환경설정(사람마다 영구)에 따로 산다.
   */
  const colState = useColState({ storageKey: 'partner-companies', initialSort: COMPANY_DEFAULT_SORT });
  const rows = useMemo(
    () => applyColState(filtered, COMPANY_COLUMNS, colState),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtered, colState.sortStack, colState.filters],
  );
  /* ⚡ 처음 목록을 빨리 보이게 (v2.222.3 · 사용자 「거래처목록도 속도 개선해줘」) — 첫 80줄 먼저 · 나머지는 한가할 때 이어 붙이기.
   *   772곳을 한 번에 그리면(줄마다 파일 올리기 칸 2개·체크박스·단추) 표 배치·그리기로 줄이 보이기까지 1초 넘게 걸렸다
   *   (헤드리스 실측 · 서버 응답은 0.09초). 규칙은 lib/useProgressiveRows.ts 한 곳(담당자 탭과 같이 쓴다).
   *   검색·유형·정렬·🔍 필터가 바뀌면 첫 줄부터. 위쪽 건수는 전체(`items`·`rows`)로 센다. */
  const listKey = useMemo(() => JSON.stringify([
    search.trim(), kindFilter, colState.sortStack,
    Object.entries(colState.filters).filter(([, s]) => s && s.size > 0).map(([k, s]) => [k, [...s].sort()]),
  ]), [search, kindFilter, colState.sortStack, colState.filters]);
  const { drawnRows, pendingRows } = useProgressiveRows(rows, listKey);
  const prefsQ =useQuery({ queryKey: ['preferences'], queryFn: prefsApi.getPreferences, staleTime: 5 * 60_000 });
  const patchPrefs = useMutation({
    mutationFn: (next: Partial<UserPreferences>) => prefsApi.patchPreferences(next),
    onSuccess: (newPrefs) => qc.setQueryData(['preferences'], newPrefs),
  });
  /** 화면에 그리는 칸 — 첨부 진행 표시·핸들러가 필요해 여기서 다시 만든다 */
  const columns = useMemo<CompanyColumn[]>(
    () => buildCompanyColumns({
      canEdit,
      busyCertId, busyBankbookId, saving: updateMut.isPending,
      onOpenDetail: (id) => setDetailId(id),
      onCertUpload: (row, file) => { void handleCertUpload(row, file); },
      onCertDelete: (row) => { void handleCertDelete(row); },
      onBankbookUpload: (row, file) => { void handleBankbookUpload(row, file); },
      onBankbookDelete: (row) => { void handleBankbookDelete(row); },
      onToggleAutoFix: (row, next) => updateMut.mutate({ id: row.id, input: { autoFix: next } }),
      onToggleActive: (row) => updateMut.mutate({ id: row.id, input: { isActive: !row.isActive } }),
      onEdit: (row) => setEditing(row),
      onDelete: (row) => handleDelete(row),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busyCertId, busyBankbookId, updateMut.isPending, canEdit],
  );
  const tbl = useExcelModeTable({
    prefs: prefsQ.data,
    fullColumns: columns as unknown as (Column<unknown> & { defaultOn: boolean })[],
    prefsKey: {
      excelMode: 'partnerCompanyTabExcelMode',
      columns: 'partnerCompanyTabColumns',
      columnsKnown: 'partnerCompanyTabColumnsKnown',
      order: 'partnerCompanyTabColumnOrder',
      widths: 'partnerCompanyTabColumnWidths',
    },
    patch: (next) => patchPrefs.mutate(next),
  });
  const visibleColumns = useMemo<CompanyColumn[]>(() => {
    const cols = tbl.visibleColumns as unknown as CompanyColumn[];
    // ⭐「액션」 칸은 끌 수 없다 — 거래처 표는 줄 클릭이 없어서, 끄면 [수정]·[삭제] 로 가는 길이 사라진다 (v2.149.0)
    if (cols.some((c) => c.key === COMPANY_ACTION_KEY)) return cols;
    const act = columns.find((c) => c.key === COMPANY_ACTION_KEY);
    return act ? [...cols, act] : cols;
  }, [tbl.visibleColumns, columns]);
  const [colMenuOpen, setColMenuOpen] = useState(false);

  const clearListState = (): void => {
    colState.resetAll();
    setSearch('');
    setKindFilter('');
  };
  const resetToDefault = (): void => {
    clearListState();
    colState.setSort(COMPANY_DEFAULT_SORT[0]!.key, COMPANY_DEFAULT_SORT[0]!.dir);
  };

  return (
    <div className="space-y-5">
      {/* 안내 */}
      <div className="bg-blue-50 border border-blue-200 rounded p-4 text-sm text-blue-900">
        <div className="font-semibold mb-1">거래처 마스터 목록</div>
        <ul className="list-disc pl-5 space-y-0.5 text-[13px] text-blue-800">
          <li>영업·매입·견적의 회사명 칸에서 이 목록으로 <strong>자동완성/선택</strong>됩니다.</li>
          <li>거래처별 <strong>사업자등록번호·주소</strong>와 <strong>사업자등록증(PDF)</strong>을 보관합니다. 📄 를 누르면 등록증을 화면에서 볼 수 있고, 그 창에서 내려받습니다.</li>
          <li><strong>자동 (주) 통일</strong> ✔ 인 거래처는, 누가 (주) 없이/기호로 입력해도 저장 시 이 표기로 자동 보정됩니다.</li>
          {!canEdit && <li><strong>조회 전용</strong> — 거래처 이름을 누르면 영업·매입·유지보수·딜·메모·문서를 한 창(거래처 360)에서 봅니다. 등록·수정·첨부는 경영지원·관리자가 합니다.</li>}
        </ul>
      </div>

      {/* 추가 — 기본 접힘, [+ 새 거래처 추가] 버튼으로만 펼침 (검색창과 혼동 방지) · 읽기 전용이면 없음(v2.318.0) */}
      {!canEdit ? null : !showAddForm ? (
        <div>
          <button
            type="button"
            onClick={openAddForm}
            className="btn btn-primary"
          >
            + 새 거래처 추가
          </button>
        </div>
      ) : (
      <div className="bg-white border border-slate-200 rounded p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-slate-700">새 거래처 추가</div>
          <button
            type="button"
            onClick={closeAddForm}
            className="text-xs text-slate-500 hover:text-slate-800"
          >
            닫기 ✕
          </button>
        </div>

        {/* 사업자등록증 사본 업로드 → 자동 판독·자동완성 */}
        <div className="mb-3 border border-indigo-200 bg-indigo-50/50 rounded p-3">
          <div className="flex items-center gap-3 flex-wrap">
            <input
              ref={certInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = ''; // 같은 파일 재선택도 onChange 가 다시 발화하도록
                if (f) void handleParseCert(f);
              }}
            />
            <button
              type="button"
              onClick={() => certInputRef.current?.click()}
              disabled={parsing}
              className="btn btn-primary"
            >
              {parsing ? '판독 중…' : '📄 사업자등록증 사본 업로드 → 자동입력'}
            </button>
            {certFile && !parsing && (
              <span className="text-xs text-slate-600">
                {certFile.name}
                <button
                  type="button"
                  onClick={() => { setCertFile(null); setParseNote(null); if (certInputRef.current) certInputRef.current.value = ''; }}
                  className="ml-1.5 text-slate-400 hover:text-red-500"
                  title="사본 선택 취소"
                >✕</button>
              </span>
            )}
            <span className="text-xs text-slate-500">PDF / 사진(JPG·PNG) — 판독 후 아래 칸을 검토·수정하고 [+ 추가]로 완료</span>
          </div>
          {parseNote && (
            <div className={`mt-2 text-xs rounded px-2.5 py-1.5 border ${
              parseNote.kind === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : parseNote.kind === 'warn' ? 'bg-amber-50 border-amber-200 text-amber-800'
              : 'bg-red-50 border-red-200 text-red-700'
            }`}>
              {parseNote.text}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <label className="ui-label block mb-1">새 거래처명</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) handleAdd(); }}
              placeholder="예: (주)서경산업"
              className="ui-input w-full"
            />
          </div>
          <div className="w-40">
            <label className="ui-label block mb-1">사업자등록번호</label>
            <input
              type="text"
              value={newBizNo}
              onChange={(e) => setNewBizNo(e.target.value)}
              placeholder="000-00-00000"
              className="ui-input w-full"
            />
          </div>
          <div className="w-28">
            <label className="ui-label block mb-1">유형</label>
            <select
              value={newKind}
              onChange={(e) => setNewKind(e.target.value as PartnerKind)}
              className="ui-input w-full"
            >
              <option value="supplier">매입처</option>
              <option value="customer">고객사</option>
              <option value="both">양쪽</option>
            </select>
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="ui-label block mb-1">주소</label>
            <input
              type="text"
              value={newAddress}
              onChange={(e) => setNewAddress(e.target.value)}
              placeholder="(선택)"
              className="ui-input w-full"
            />
          </div>
          <div className="w-52">
            <label className="ui-label block mb-1">세금계산서 이메일 ①</label>
            <input
              type="text"
              value={newEmail1}
              onChange={(e) => setNewEmail1(e.target.value)}
              placeholder="(선택) tax@…"
              className="ui-input w-full"
            />
          </div>
          <div className="w-52">
            <label className="ui-label block mb-1">세금계산서 이메일 ②</label>
            <input
              type="text"
              value={newEmail2}
              onChange={(e) => setNewEmail2(e.target.value)}
              placeholder="(선택)"
              className="ui-input w-full"
            />
          </div>
          <label className="flex items-center gap-1.5 text-sm text-slate-700 pb-1.5">
            <input type="checkbox" checked={newAutoFix} onChange={(e) => setNewAutoFix(e.target.checked)} />
            자동 (주) 통일
          </label>
          <button
            type="button"
            onClick={handleAdd}
            disabled={createMut.isPending || parsing || !newName.trim()}
            className="btn btn-primary"
          >
            {createMut.isPending ? '등록 중…' : '+ 추가'}
          </button>
        </div>
      </div>
      )}

      {/* NAS 보관 결과 — 표 바깥 한 줄 (v2.96.0). 칸 안에 넣으면 행 높이가 흔들린다. */}
      {nasNote && (
        <div
          className={`text-xs rounded px-3 py-2 border ${
            nasNote.startsWith('⚠')
              ? 'bg-amber-50 border-amber-200 text-amber-800'
              : 'bg-emerald-50 border-emerald-200 text-emerald-800'
          }`}
        >
          {nasNote}
        </div>
      )}

      {/* 검색 + 유형필터 + 카운트 */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="거래처명·사업자번호·주소 검색…"
            className="ui-input w-64"
          />
          <select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as '' | PartnerKind)}
            className="ui-input"
          >
            <option value="">유형 전체</option>
            <option value="supplier">매입처</option>
            <option value="customer">고객사</option>
            <option value="both">양쪽</option>
          </select>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* 정렬·필터 초기화 · 📐 내 표 설정 (v2.148.0 · 담당자 탭·딜등록과 같은 자리) */}
          <ResetMenu
            defaultLabel="거래처명 오름차순 · 검색·유형·필터 없음"
            onResetDefault={resetToDefault}
            onResetAll={clearListState}
          />
          <ExcelModeGroup
            enabled={tbl.excelMode}
            hasPrefs={tbl.hasPrefs}
            userName={user?.name ?? '사용자'}
            onChange={tbl.setExcelMode}
            onReset={tbl.handleReset}
          />
        </div>
        <div className="text-xs text-slate-500 w-full">
          총 <strong>{items.length}</strong> · 매입처 <strong className="text-sky-700">{kindCounts.supplier}</strong> · 고객사 <strong className="text-amber-700">{kindCounts.customer}</strong> · 양쪽 <strong className="text-violet-700">{kindCounts.both}</strong> · 사업자등록증 <strong className="text-emerald-700">{certCount}</strong> · 통장사본 <strong className="text-teal-700">{bankbookCount}</strong>
          {(search.trim() || kindFilter || rows.length !== filtered.length) && <> · 결과 {rows.length}</>}
          {' '}· 자동통일 {autoFixCount}
        </div>
      </div>

      {/* ⚙ 표시 컬럼 — [📐 내 표 설정] 을 켰을 때만 (좁은 화면에서는 숨긴다) */}
      {tbl.excelMode ? (
        <div className="hidden md:block bg-white rounded border border-slate-200 px-3 py-2 text-xs">
          <div className="flex items-center justify-between">
            <button type="button" onClick={() => setColMenuOpen((v) => !v)} className="text-blue-600 hover:underline">
              ⚙ 표시 컬럼 ({visibleColumns.length}/{tbl.fullAllKeys.length}{patchPrefs.isPending ? ' · 저장 중...' : ''}){colMenuOpen ? ' ▲' : ' ▼'}
            </button>
            <span className="text-xs text-slate-500">💡 머리글을 끌면 순서 변경 · 우측 끝을 끌면 너비 조정 · 머리글 클릭은 정렬 (자동 저장)</span>
          </div>
          {colMenuOpen ? (
            <div className="mt-2 grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1">
              {/* 「액션」 칸은 목록에서 뺀다 — 끌 수 없는 칸이라 체크박스를 보이면 헷갈린다 (v2.149.0) */}
              {columns.filter((d) => d.key !== COMPANY_ACTION_KEY).map((d) => {
                const on = tbl.activeColKeys.has(d.key);
                return (
                  <label key={d.key} className={`flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer text-xs ${on ? 'bg-blue-50 text-blue-800' : 'text-slate-500 hover:bg-slate-50'}`}>
                    <input type="checkbox" checked={on} onChange={() => tbl.toggleColumn(d.key)} disabled={patchPrefs.isPending} />
                    <span>{d.toggleLabel ?? d.label}</span>
                  </label>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* 목록 */}
      <div className="bg-white rounded shadow-sm border border-slate-200 overflow-x-auto">
        <table className="data-table dense w-full text-[13px]">
          <thead>
            <tr>
              {visibleColumns.map((col, idx) => (
                <ColumnHeader
                  key={col.key}
                  column={col}
                  baseRows={filtered}
                  /* ⭐연쇄 필터·숨긴 칸 필터가 살아 있으려면 **전체 칸**을 넘겨야 한다 */
                  columns={COMPANY_COLUMNS}
                  colState={colState}
                  excelMode={tbl.excelMode}
                  onReorder={tbl.excelMode ? tbl.handleReorder : undefined}
                  onMoveLeft={tbl.excelMode && idx > 0 ? () => tbl.handleMoveLeft(col.key) : undefined}
                  onMoveRight={tbl.excelMode && idx < visibleColumns.length - 1 ? () => tbl.handleMoveRight(col.key) : undefined}
                  onResize={tbl.excelMode ? tbl.handleResize : undefined}
                  widthPx={tbl.columnWidths?.[col.key] ?? col.defaultWidthPx}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr><td colSpan={Math.max(visibleColumns.length, 1)} className="text-center py-8 text-slate-400"><LoadingLines /></td></tr>
            )}
            {q.isError && (
              <tr><td colSpan={Math.max(visibleColumns.length, 1)} className="text-center py-8 text-red-500">오류: {(q.error as Error).message}</td></tr>
            )}
            {q.data && rows.length === 0 && (
              <tr><td colSpan={Math.max(visibleColumns.length, 1)} className="text-center py-8 text-slate-400">
                {search.trim() || kindFilter || filtered.length !== rows.length ? '조건에 맞는 거래처가 없습니다.' : '등록된 거래처가 없습니다.'}
              </td></tr>
            )}
            {drawnRows.map((row) => (
              <tr key={row.id} className={row.isActive ? '' : 'bg-slate-50 text-slate-400'}>
                {visibleColumns.map((col) => (
                  <td
                    key={col.key}
                    className={`${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : 'text-left'} ${col.cellClass ?? ''} ${col.mobileHidden ? 'hidden md:table-cell' : ''}`}
                  >
                    {col.render ? col.render(row) : (col.accessor(row) ?? '')}
                  </td>
                ))}
              </tr>
            ))}
            {/* ⚡ 아직 안 그린 줄 — 곧 이어 붙는다. 빨리 끝까지 내린 사람이 「목록이 여기서 끝났나」로 오해하지 않게 (v2.222.3) */}
            {q.data && pendingRows > 0 && (
              <tr><td colSpan={Math.max(visibleColumns.length, 1)} className="text-center py-3 text-slate-400">나머지 {pendingRows}곳 불러오는 중…</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-slate-500">
        {rows.length}건{rows.length !== filtered.length ? ` (필터 전 ${filtered.length}건)` : ''} ·
        머리글을 누르면 정렬(다시 누르면 반대, 한 번 더 누르면 해제) · 머리글 옆 🔍 로 값 골라 보기 ·
        [📐 내 표 설정] 을 켜면 칸을 고르고 순서·너비를 바꿀 수 있습니다
      </div>

      {editing && (
        <PartnerEditModal
          row={editing}
          onClose={() => setEditing(null)}
          onSave={(input) => {
            updateMut.mutate({ id: editing.id, input }, { onSuccess: () => setEditing(null) });
          }}
          saving={updateMut.isPending}
        />
      )}

      {detailId != null && (
        <PartnerDetailModal id={detailId} onClose={() => setDetailId(null)} />
      )}
    </div>
  );
}

// 사업자등록증 셀 — 📄 다운로드 + 첨부/교체/삭제
function CertCell({
  row, canEdit, busy, onUpload, onDelete,
}: {
  row: PartnerCompanyRow;
  canEdit: boolean;
  busy: boolean;
  onUpload: (row: PartnerCompanyRow, file: File) => void;
  onDelete: (row: PartnerCompanyRow) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  // 읽기 전용(영업 · v2.318.0) — 보기만. 첨부·교체·삭제 단추는 없다(서버도 admin·mgmt 만 받는다)
  if (!canEdit) {
    return row.certAttachmentId ? (
      <button
        type="button"
        onClick={() => previewAttachment({ id: row.certAttachmentId!, filename: row.certFilename ?? `사업자등록증_${row.name}.pdf` })}
        className="text-blue-700 hover:underline"
        title={`사업자등록증 보기 (${row.certFilename ?? ''})`}
      >
        📄
      </button>
    ) : (
      <span className="text-slate-300">-</span>
    );
  }
  return (
    <div className="flex items-center justify-center gap-1.5">
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(row, f); e.target.value = ''; }}
      />
      {row.certAttachmentId ? (
        <>
          <button
            type="button"
            onClick={() => previewAttachment({ id: row.certAttachmentId!, filename: row.certFilename ?? `사업자등록증_${row.name}.pdf` })}
            className="text-base leading-none hover:scale-110 transition-transform"
            title={`사업자등록증 보기 (${row.certFilename ?? ''})`}
          >
            📄
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            className="text-xs text-slate-500 hover:text-blue-600 disabled:opacity-40"
            title="다른 PDF로 교체"
          >
            교체
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onDelete(row)}
            className="text-xs text-slate-400 hover:text-red-500 disabled:opacity-40"
            title="첨부 삭제"
          >
            ✕
          </button>
        </>
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          className="btn btn-secondary btn-sm"
          title="사업자등록증 PDF 첨부"
        >
          {busy ? '…' : '📎 첨부'}
        </button>
      )}
    </div>
  );
}

// 통장사본 셀 — 📗 보기 + 첨부/교체/삭제 (사업자등록증과 동일 방식, kind='통장사본')
function BankbookCell({
  row, busy, onUpload, onDelete,
}: {
  row: PartnerCompanyRow;
  busy: boolean;
  onUpload: (row: PartnerCompanyRow, file: File) => void;
  onDelete: (row: PartnerCompanyRow) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="flex items-center justify-center gap-1.5">
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(row, f); e.target.value = ''; }}
      />
      {row.bankbookAttachmentId ? (
        <>
          <button
            type="button"
            onClick={() => previewAttachment({ id: row.bankbookAttachmentId!, filename: row.bankbookFilename ?? `통장사본_${row.name}.pdf` })}
            className="text-base leading-none hover:scale-110 transition-transform"
            title={`통장사본 보기 (${row.bankbookFilename ?? ''})`}
          >
            📗
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            className="text-xs text-slate-500 hover:text-blue-600 disabled:opacity-40"
            title="다른 파일로 교체"
          >
            교체
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onDelete(row)}
            className="text-xs text-slate-400 hover:text-red-500 disabled:opacity-40"
            title="첨부 삭제"
          >
            ✕
          </button>
        </>
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          className="btn btn-secondary btn-sm"
          title="통장사본 첨부 (PDF·이미지)"
        >
          {busy ? '…' : '📎 첨부'}
        </button>
      )}
    </div>
  );
}

// 거래처 수정 모달 — 거래처명·사업자등록번호·주소
function PartnerEditModal({
  row, onClose, onSave, saving,
}: {
  row: PartnerCompanyRow;
  onClose: () => void;
  onSave: (input: {
    name?: string; businessNumber?: string | null; address?: string | null; kind?: PartnerKind;
    taxInvoiceEmail?: string | null; taxInvoiceEmail2?: string | null;
  }) => void;
  saving: boolean;
}) {
  const [name, setName] = useState(row.name);
  const [bizNo, setBizNo] = useState(row.businessNumber ?? '');
  const [address, setAddress] = useState(row.address ?? '');
  const [kind, setKind] = useState<PartnerKind>(row.kind);
  const [email1, setEmail1] = useState(row.taxInvoiceEmail ?? '');
  const [email2, setEmail2] = useState(row.taxInvoiceEmail2 ?? '');

  function handleSave() {
    const nm = name.trim();
    if (!nm) { alert('거래처명을 입력하세요'); return; }
    onSave({
      name: nm, businessNumber: bizNo.trim() || null, address: address.trim() || null, kind,
      taxInvoiceEmail: email1.trim() || null, taxInvoiceEmail2: email2.trim() || null,
    });
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="ui-subsection-title">거래처 수정</h3>
          <button type="button" onClick={onClose} className="ui-close">×</button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="ui-label block mb-1">거래처명</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              className="ui-input w-full" />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="ui-label block mb-1">사업자등록번호</label>
              <input type="text" value={bizNo} onChange={(e) => setBizNo(e.target.value)} placeholder="000-00-00000"
                className="ui-input w-full font-mono" />
            </div>
            <div className="w-32">
              <label className="ui-label block mb-1">유형</label>
              <select value={kind} onChange={(e) => setKind(e.target.value as PartnerKind)}
                className="ui-input w-full">
                <option value="supplier">매입처</option>
                <option value="customer">고객사</option>
                <option value="both">양쪽</option>
              </select>
            </div>
          </div>
          <div>
            <label className="ui-label block mb-1">주소</label>
            <textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2}
              className="ui-input ui-textarea w-full" />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="ui-label block mb-1">세금계산서 이메일 ①</label>
              <input type="text" value={email1} onChange={(e) => setEmail1(e.target.value)} placeholder="tax@example.com"
                className="ui-input w-full" />
            </div>
            <div className="flex-1">
              <label className="ui-label block mb-1">세금계산서 이메일 ②</label>
              <input type="text" value={email2} onChange={(e) => setEmail2(e.target.value)} placeholder="(선택)"
                className="ui-input w-full" />
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn btn-ghost">취소</button>
          <button type="button" onClick={handleSave} disabled={saving}
            className="btn btn-primary">
            {saving ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
